// src/pages/programs/ProgramRegisterSuccess.jsx
// Landing page after a successful program registration (with or without
// payment). Mirrors the lime/pitch identity used by RegisterSuccess +
// the in-page success state in RegisterPage so the parent's confirmation
// is visually consistent regardless of which registration path they came
// from (season vs program). Waitlist branch keeps amber accents because
// "you're on the list" reads differently than "you're confirmed."
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import {
  CheckCircleIcon,
  ClockIcon,
  EnvelopeIcon,
  DocumentArrowUpIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

export default function ProgramRegisterSuccess() {
  const { programId } = useParams();
  const [searchParams] = useSearchParams();
  const regId = searchParams.get('regId');

  const [program, setProgram] = useState(null);
  const [reg, setReg] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [progSnap, regSnap] = await Promise.all([
          getDoc(doc(db, 'programs', programId)),
          regId ? getDoc(doc(db, 'registrations', regId)) : Promise.resolve(null),
        ]);
        if (progSnap.exists()) setProgram({ id: progSnap.id, ...progSnap.data() });
        if (regSnap?.exists()) setReg({ id: regSnap.id, ...regSnap.data() });
      } catch (e) {
        console.error('ProgramRegisterSuccess load error:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [programId, regId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7faf5]">
        <div className="text-zinc-500 text-sm">Loading…</div>
      </div>
    );
  }

  const isWaitlist = reg?.status === 'waitlist';

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-16 bg-[#f7faf5]">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] border border-zinc-200 p-8 text-center space-y-5">
          {/* Icon — lime medallion for confirmation, amber for waitlist
              (waitlist isn't a "you're done!" moment). */}
          <div className="flex justify-center">
            <div className={`rounded-full p-4 ${
              isWaitlist
                ? 'bg-amber-100 ring-1 ring-amber-200'
                : 'bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40'
            }`}>
              {isWaitlist
                ? <ClockIcon className="h-12 w-12 text-amber-700" />
                : <CheckCircleIcon className="h-12 w-12 text-[#5a8a1f]" />
              }
            </div>
          </div>

          {/* Title */}
          <div>
            <p
              className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
              style={{ fontFamily: HEAD_FONT }}
            >
              {isWaitlist ? 'Waitlisted' : 'Registration confirmed'}
            </p>
            <h1
              className="text-2xl font-bold uppercase tracking-tight text-[#0d1f14] mt-1"
              style={{ fontFamily: HEAD_FONT }}
            >
              {isWaitlist ? "You're on the Waitlist!" : "You're Registered!"}
            </h1>
            {program && (
              <p className="text-zinc-600 text-sm mt-1">{program.name}</p>
            )}
          </div>

          {/* Details */}
          {reg && (
            <div className="rounded-xl bg-zinc-50 border border-zinc-200 divide-y divide-zinc-200 text-left">
              {reg.playerName && (
                <div className="flex items-center justify-between px-4 py-3">
                  <span
                    className="text-[10px] font-bold uppercase tracking-[1.2px] text-zinc-500"
                    style={{ fontFamily: HEAD_FONT }}
                  >
                    Player
                  </span>
                  <span className="text-sm font-semibold text-[#0d1f14]">{reg.playerName}</span>
                </div>
              )}
              {reg.divisionId && program?.divisions && (
                <div className="flex items-center justify-between px-4 py-3">
                  <span
                    className="text-[10px] font-bold uppercase tracking-[1.2px] text-zinc-500"
                    style={{ fontFamily: HEAD_FONT }}
                  >
                    Division
                  </span>
                  <span className="text-sm font-semibold text-[#0d1f14]">
                    {program.divisions.find((d) => d.id === reg.divisionId)?.name ?? reg.divisionId}
                  </span>
                </div>
              )}
              {reg.parentEmail && (
                <div className="flex items-center justify-between px-4 py-3 gap-3">
                  <span
                    className="text-[10px] font-bold uppercase tracking-[1.2px] text-zinc-500 shrink-0"
                    style={{ fontFamily: HEAD_FONT }}
                  >
                    Contact
                  </span>
                  <span className="text-sm font-semibold text-[#0d1f14] truncate">{reg.parentEmail}</span>
                </div>
              )}
              {isWaitlist && (
                <div className="flex items-center justify-between px-4 py-3">
                  <span
                    className="text-[10px] font-bold uppercase tracking-[1.2px] text-zinc-500"
                    style={{ fontFamily: HEAD_FONT }}
                  >
                    Status
                  </span>
                  <span className="text-sm font-bold uppercase text-amber-700"
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                  >
                    Waitlisted
                  </span>
                </div>
              )}
              {!isWaitlist && reg.pricing?.finalAmount > 0 && reg.status === 'confirmed' && (
                <div className="flex items-center justify-between px-4 py-3">
                  <span
                    className="text-[10px] font-bold uppercase tracking-[1.2px] text-zinc-500"
                    style={{ fontFamily: HEAD_FONT }}
                  >
                    Amount paid
                  </span>
                  <span className="text-sm font-bold text-emerald-700"
                    style={{ fontFamily: HEAD_FONT }}
                  >
                    ${(reg.pricing.finalAmount / 100).toFixed(2)}
                  </span>
                </div>
              )}
              {!isWaitlist && reg.pricing?.finalAmount > 0 && reg.status === 'pending_payment' && (
                <div className="flex items-center justify-between px-4 py-3">
                  <span
                    className="text-[10px] font-bold uppercase tracking-[1.2px] text-zinc-500"
                    style={{ fontFamily: HEAD_FONT }}
                  >
                    Payment
                  </span>
                  <span className="text-sm font-bold uppercase text-amber-700"
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                  >
                    Pending
                  </span>
                </div>
              )}
            </div>
          )}

          {/* What's next — lime themed for confirmed, amber for waitlist
              (different emotional register, different color cues). */}
          <div className={`rounded-xl border p-4 text-left space-y-2 ${
            isWaitlist
              ? 'bg-amber-50 border-amber-200'
              : 'bg-[#b5f03d]/10 border-[#b5f03d]/40'
          }`}>
            <p
              className={`text-[11px] font-bold uppercase tracking-[1.4px] ${
                isWaitlist ? 'text-amber-800' : 'text-[#3d6010]'
              }`}
              style={{ fontFamily: HEAD_FONT }}
            >
              What's next?
            </p>
            <ul className={`space-y-1.5 text-xs ${isWaitlist ? 'text-amber-800' : 'text-[#3d6010]'}`}>
              {isWaitlist ? (
                <>
                  <li className="flex items-start gap-2">
                    <ClockIcon className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>You've been added to the waitlist for this division</span>
                  </li>
                  {reg?.parentEmail && (
                    <li className="flex items-start gap-2">
                      <EnvelopeIcon className="h-4 w-4 shrink-0 mt-0.5" />
                      <span>We'll notify <strong>{reg.parentEmail}</strong> if a spot opens up</span>
                    </li>
                  )}
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5">📋</span>
                    <span>No payment is required until you're moved off the waitlist</span>
                  </li>
                </>
              ) : (
                <>
                  {reg?.parentEmail && (
                    <li className="flex items-start gap-2">
                      <EnvelopeIcon className="h-4 w-4 shrink-0 mt-0.5" />
                      <span>A confirmation will be sent to <strong>{reg.parentEmail}</strong></span>
                    </li>
                  )}
                  {program?.requiredDocTypes?.length > 0 && (
                    <li className="flex items-start gap-2">
                      <span className="mt-0.5">📄</span>
                      <span>
                        You'll need to upload:{' '}
                        {program.requiredDocTypes.join(', ')}
                      </span>
                    </li>
                  )}
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5">⚽</span>
                    <span>Your coach will be in touch with team details soon</span>
                  </li>
                </>
              )}
            </ul>
          </div>

          {/* Upload CTA — surfaced inline so the parent acts at the
              moment of highest attention (right after registering)
              instead of having to find the link later in the email.
              Same UX pattern as season RegisterSuccess. Skipped on
              waitlist since a waitlisted parent doesn't owe docs
              until they're moved into a confirmed spot. */}
          {!isWaitlist && program?.requiredDocTypes?.length > 0 && regId && (
            <div className="rounded-xl border border-[#b5f03d]/40 bg-[#b5f03d]/10 p-4">
              <div className="flex items-start gap-3 mb-3 text-left">
                <div className="h-8 w-8 rounded-lg bg-[#b5f03d]/30 flex items-center justify-center flex-shrink-0">
                  <DocumentArrowUpIcon className="h-4 w-4 text-[#0d1f14]" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-[#0d1f14] leading-tight">
                    One more step: upload required documents
                  </p>
                  <p className="mt-1 text-xs text-zinc-600 leading-snug">
                    {program.requiredDocTypes.length === 1
                      ? "Your coach needs a copy of the eligibility document to confirm your player."
                      : `Your coach needs ${program.requiredDocTypes.length} eligibility documents to confirm your player.`}
                  </p>
                </div>
              </div>
              <Link
                to={`/upload/${regId}`}
                className="inline-flex items-center gap-1.5 rounded-[10px] bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-3 py-2 text-xs font-bold uppercase transition-colors"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
              >
                <DocumentArrowUpIcon className="h-3.5 w-3.5" />
                Upload documents now
                <ArrowRightIcon className="h-3 w-3" />
              </Link>
            </div>
          )}

          <p className="text-xs text-zinc-500">
            Registration #{regId ? regId.slice(-8).toUpperCase() : '—'}
          </p>
        </div>

        <p className="text-center text-xs text-zinc-500 mt-6">
          Powered by <span className="font-bold uppercase text-[#5a8a1f]" style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}>Scrimly</span>
        </p>
      </div>
    </div>
  );
}
